import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { RoleBadges } from "@/components/RoleBadges";
import { QRCodeCanvas } from "qrcode.react";
import { initials } from "@/lib/format";
import { accentVars } from "@/lib/cardThemes";
import {
  Mail, Phone, Globe, Linkedin, MapPin, QrCode, Pencil, RefreshCcw,
  Sparkles, Target, Briefcase, Tag,
} from "lucide-react";

// Brand-consistent QR: high-contrast navy on white for reliable scanning.
const QR_FG = "#082D50";
const QR_BG = "#FFFFFF";

const stop = (fn) => (e) => {
  e.stopPropagation();
  fn?.(e);
};

function ContactLine({ icon: Icon, children, testId }) {
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={testId}>
      <Icon className="h-4 w-4 shrink-0 text-primary" />
      <span className="min-w-0 truncate text-foreground">{children}</span>
    </div>
  );
}

function FoilEdge() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-y-0 left-0 z-10 w-1.5 opacity-90"
      style={{ backgroundImage: "linear-gradient(180deg, hsl(var(--fc-accent-2)), hsl(var(--fc-accent)))" }}
    />
  );
}

function MemberCardFront({ member, shareUrl, qrSize }) {
  const subtitle = [member.title, member.company].filter(Boolean).join(" · ");
  const contacts = [
    member.email && <ContactLine key="e" icon={Mail} testId="member-card-email">{member.email}</ContactLine>,
    member.phone && <ContactLine key="p" icon={Phone} testId="member-card-phone">{member.phone}</ContactLine>,
    member.website && <ContactLine key="w" icon={Globe} testId="member-card-website">{member.website.replace(/^https?:\/\//, "")}</ContactLine>,
    member.linkedin && <ContactLine key="l" icon={Linkedin} testId="member-card-linkedin">LinkedIn</ContactLine>,
    member.location_text && <ContactLine key="loc" icon={MapPin} testId="member-card-location">{member.location_text}</ContactLine>,
  ].filter(Boolean);

  return (
    <div className="relative overflow-hidden rounded-[calc(var(--radius)+6px)] border bg-card">
      <FoilEdge />
      {member.card_cover ? (
        <div className="relative h-20 w-full sm:h-24" data-testid="member-card-cover">
          <img src={member.card_cover} alt="" className="h-full w-full object-cover" />
          <span aria-hidden className="absolute inset-0" style={{ backgroundImage: "linear-gradient(to top, hsl(var(--card) / 0.55), transparent 55%)" }} />
        </div>
      ) : null}
      <div className="relative p-4 sm:p-5">
        <span aria-hidden className="fc-paper-noise pointer-events-none absolute inset-0 opacity-60" />
        <div className="relative">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <Avatar className="h-12 w-12 shrink-0 ring-2 ring-[hsl(var(--fc-accent)/0.45)] sm:h-14 sm:w-14" data-testid="member-card-avatar">
              <AvatarImage src={member.avatar || undefined} alt={member.name} />
              <AvatarFallback className="bg-primary text-primary-foreground">{initials(member.name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h2 className="truncate text-xl font-semibold leading-tight tracking-tight sm:text-2xl" data-testid="member-card-name">{member.name}</h2>
              {member.headline ? (
                <p className="mt-0.5 line-clamp-2 text-sm font-medium text-muted-foreground sm:text-base" data-testid="member-card-headline">{member.headline}</p>
              ) : (
                <p className="mt-0.5 truncate text-sm text-muted-foreground">{subtitle || "Friday Conversations member"}</p>
              )}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <span data-testid="member-card-role-badge">
              <RoleBadges roles={member.roles} is_owner={member.is_owner} podcast_eligible={member.podcast_eligible} showMember size="sm" />
            </span>
            {member.business?.logo ? (
              <img src={member.business.logo} alt="" className="max-h-7 w-auto rounded object-contain" />
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex flex-col items-center">
          <div className="rounded-xl border bg-white p-3 shadow-sm" data-testid="member-card-qr">
            <QRCodeCanvas value={shareUrl || " "} size={qrSize} fgColor={QR_FG} bgColor={QR_BG} level="M" marginSize={2} />
          </div>
          <p className="mt-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground" data-testid="member-card-qr-caption">
            Scan to save my card
          </p>
        </div>

        {contacts.length > 0 && (
          <div className="mt-4 grid grid-cols-1 gap-1.5 rounded-lg border bg-background/40 p-3 sm:grid-cols-2">
            {contacts}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function MemberCardBack({ member, showActions, onPresent, onEdit, onFlip }) {
  const interests = (member.interests || []).slice(0, 8);
  const extra = (member.interests || []).length - interests.length;
  const biz = member.business;
  const hasAbout = member.bio || member.headline;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-[calc(var(--radius)+6px)] border bg-card p-4 sm:p-5">
      <FoilEdge />
      <span aria-hidden className="fc-paper-noise pointer-events-none absolute inset-0 opacity-60" />
      <div className="relative flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
        {hasAbout && (
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">About</p>
            {member.bio && <p className="mt-1 line-clamp-3 text-sm leading-6 text-foreground" data-testid="member-card-bio">{member.bio}</p>}
            {member.headline && (
              <p className="mt-1.5 flex items-start gap-1.5 text-sm text-muted-foreground" data-testid="member-card-ask-me-about">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="line-clamp-2">{member.headline}</span>
              </p>
            )}
          </div>
        )}

        {interests.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Interests</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="member-card-interests">
              {interests.map((t) => (
                <Badge key={t} variant="secondary" className="border border-border text-xs"><Tag className="mr-1 h-3 w-3" />{t}</Badge>
              ))}
              {extra > 0 && <Badge variant="secondary" className="text-xs">+{extra}</Badge>}
            </div>
          </div>
        )}

        {member.seeking && (
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Looking for</p>
            <p className="mt-1 flex items-start gap-1.5 text-sm text-foreground" data-testid="member-card-looking-for">
              <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="line-clamp-2">{member.seeking}</span>
            </p>
          </div>
        )}

        {biz && biz.business_name && (
          <div className="mt-auto rounded-lg border bg-background/40 p-2.5">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Briefcase className="h-4 w-4 text-primary" />
              <span className="truncate">{biz.business_name}</span>
              {biz.industry && <Badge variant="secondary" className="ml-auto shrink-0 text-[10px]">{biz.industry}</Badge>}
            </div>
            {biz.tagline && <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{biz.tagline}</p>}
          </div>
        )}
      </div>

      {showActions && (
        <>
          <Separator className="relative my-3" />
          <div className="relative flex items-center justify-between gap-2">
            <Button size="sm" onClick={stop(onPresent)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="member-card-present-button">
              <QrCode className="mr-1.5 h-4 w-4" /> Present
            </Button>
            <Button size="sm" variant="secondary" onClick={stop(onEdit)} data-testid="member-card-edit-button">
              <Pencil className="mr-1.5 h-4 w-4" /> Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={stop(onFlip)} data-testid="member-card-flip-button">
              <RefreshCcw className="mr-1.5 h-4 w-4" /> Flip
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Premium flip member card. The FRONT (identity + big QR) defines the height;
 * the BACK overlays it. Tap/click anywhere (except buttons) to flip.
 */
export function MemberCardFlip({
  member,
  shareUrl,
  flipped = false,
  onToggleFlip,
  onPresent,
  onEdit,
  showActions = true,
  qrSize = 200,
  className = "",
}) {
  if (!member) return null;
  const toggle = () => onToggleFlip?.(!flipped);

  return (
    <div className={`fc-flip mx-auto w-full max-w-[520px] ${className}`} style={accentVars(member.card_accent)} data-testid="member-card-flip">
      <div className="fc-flip-inner rounded-[calc(var(--radius)+6px)] shadow-[var(--fc-card-shadow)]" data-flipped={flipped ? "true" : "false"}>
        <div
          role="button"
          tabIndex={flipped ? -1 : 0}
          aria-pressed={flipped}
          aria-label="Member card front — tap to flip"
          onClick={toggle}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
          className="fc-flip-front cursor-pointer rounded-[calc(var(--radius)+6px)] outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <MemberCardFront member={member} shareUrl={shareUrl} qrSize={qrSize} />
        </div>
        <div
          role="button"
          tabIndex={flipped ? 0 : -1}
          aria-label="Member card back — tap to flip"
          onClick={toggle}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
          className="fc-flip-back cursor-pointer rounded-[calc(var(--radius)+6px)] outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <MemberCardBack member={member} showActions={showActions} onPresent={onPresent} onEdit={onEdit} onFlip={toggle} />
        </div>
      </div>
    </div>
  );
}
